import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ButtonList, DropdownButton, DropdownButtonContext, Page, } from '@dixa/lib-shared-habla';
import { DashboardQueuesType } from 'lib-shared-legacy-types';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { analytics } from 'app/utils/segment';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { dashboardActions } from '../actions/dashboard';
import QueuesOverview from './QueuesOverview/QueuesOverview';
// An array of valid API params to check against due to lack of ability to
// compare against keys of an interface, without introducing TS transformers.
const VALID_API_QUERY_PARAMS = ['limit', 'offset', 'search', 'sort_by'];
export const Dashboard = (props) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { archiveQueues } = useFeatureFlag(['archive-queues']);
    const getQueues = () => {
        const { favouriteQueueIds, getAllQueues, getAgentQueues, getQueuesById, dashboardChangeQueuesType, } = props;
        const currentQueuesType = getCurrentQueuesType();
        const apiQueryParams = getValidApiQueryParams();
        if (archiveQueues) {
            apiQueryParams.is_archived =
                currentQueuesType === DashboardQueuesType.Archived;
        }
        dashboardChangeQueuesType(currentQueuesType);
        if ([DashboardQueuesType.All, DashboardQueuesType.Archived].includes(currentQueuesType)) {
            getAllQueues(apiQueryParams);
        }
        else if (currentQueuesType === DashboardQueuesType.Agent) {
            getAgentQueues(apiQueryParams);
        }
        else if (currentQueuesType === DashboardQueuesType.Favourite &&
            !!favouriteQueueIds.length) {
            getQueuesById({
                queue_ids: favouriteQueueIds.filter(Boolean).join(','),
                sort_by: props.meta.currentPageParams.sort_by,
            });
        }
    };
    useEffect(() => {
        getQueues();
    }, [searchParams]);
    useEffect(() => {
        props.wsRequest({
            type: 'connection:subscribe',
            params: {
                type: 'queue_dashboard',
            },
        });
        return () => {
            props.wsRequest({
                type: 'connection:unsubscribe',
                params: {
                    type: 'queue_dashboard',
                },
            });
        };
    }, []);
    const queuesTypeDropdownItems = useMemo(() => [
        {
            type: DashboardQueuesType.All,
            label: 'All queues',
        },
        {
            type: DashboardQueuesType.Agent,
            label: 'My queues',
        },
        {
            type: DashboardQueuesType.Favourite,
            label: 'Favorite queues',
        },
        ...(archiveQueues
            ? [
                {
                    type: DashboardQueuesType.Archived,
                    label: 'Archived queues',
                },
            ]
            : []),
    ], [archiveQueues]);
    const getCurrentQueuesType = () => {
        var _a;
        const { meta: { currentPageParams: { type: stateType }, }, } = props;
        const query = queryString.parse(location.search);
        const selectedQueueType = window.localStorage.getItem('selected-queue-type');
        const currentQueuesType = ((_a = queuesTypeDropdownItems.find(({ type }) => type === query.type)) === null || _a === void 0 ? void 0 : _a.type) ||
            selectedQueueType ||
            stateType;
        return currentQueuesType;
    };
    /** Gets the valid API endpoint query params from current location query. */
    const getValidApiQueryParams = () => {
        const { meta: { currentPageParams }, } = props;
        const query = queryString.parse(location.search);
        return Object.keys(query)
            .filter((key) => VALID_API_QUERY_PARAMS.includes(key))
            .reduce((params, key) => (Object.assign(Object.assign({}, params), { [key]: query[key] })), currentPageParams);
    };
    const updateCurrentQueuesType = (type) => {
        props.dashboardChangeQueuesType(type);
        const newLocationParams = {
            type,
            sort_by: props.meta.currentPageParams.sort_by,
        };
        const newSearchParams = new URLSearchParams(newLocationParams);
        window.localStorage.setItem('selected-queue-type', type);
        setSearchParams(newSearchParams);
    };
    const currentQueuesType = getCurrentQueuesType();
    const currentQueuesTypeItem = queuesTypeDropdownItems.find((item) => item.type === currentQueuesType);
    const queuesTypeDropdown = (_jsx(DropdownButton, Object.assign({ buttonProps: {
            variant: 'tertiary',
        }, dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsx(ButtonList, { children: queuesTypeDropdownItems.map(({ type, label }) => {
                    const isCurrentQueuesType = currentQueuesType === type;
                    return (_jsx(ButtonList.Item, Object.assign({ isSelected: isCurrentQueuesType, onClick: () => {
                            if (isCurrentQueuesType)
                                return;
                            toggleDropdown();
                            updateCurrentQueuesType(type);
                            analytics.dashboardQueuesTypeChanged({
                                type,
                                location: 'dropdown',
                            });
                        } }, { children: label }), type));
                }) })) }) }, { children: currentQueuesTypeItem === null || currentQueuesTypeItem === void 0 ? void 0 : currentQueuesTypeItem.label })));
    return (_jsxs(Page, { children: [_jsx(Page.Head, { title: "Queue Dashboard", controls: queuesTypeDropdown }), _jsx(Page.Body, { children: _jsx(QueuesOverview, { onQueuesTypeChange: updateCurrentQueuesType }) })] }));
};
const mapStateToProps = (state) => {
    const { settings: { personal: { favouriteQueueIds }, }, dashboard, } = state;
    return Object.assign(Object.assign({}, dashboard), { favouriteQueueIds });
};
const mapDispatchToProps = (dispatch) => {
    return {
        dashboardChangeQueuesType: (type) => dispatch(dashboardActions.dashboardChangeQueuesType({ type })),
        getAllQueues: (params) => dispatch(dashboardActions.dashboardGetAllQueues(params)),
        getAgentQueues: (params) => dispatch(dashboardActions.dashboardGetAgentQueues(params)),
        getQueuesById: (params) => dispatch(dashboardActions.dashboardGetQueuesById(params)),
        wsRequest: bindRtkActionToPromise(dispatch, websocketActions.request),
    };
};
export const DashboardConnected = connect(mapStateToProps, mapDispatchToProps)(Dashboard);
